import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Hash, Globe, Store, TrendingUp, MapPin, Users } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { SkeletonList, SectionHead, Empty } from '../components/ui';
import { CONTINENTS, flagOf } from '../lib/countries';

export default function Groups() {
  const { city, country, user } = useAuth();
  const [q, setQ] = useState('');
  const [continent, setContinent] = useState('');
  const [tab, setTab] = useState('cities');
  const isAdmin = user?.role === 'admin';

  const { data: cities, isLoading } = useQuery({
    queryKey: ['browseCities', q, continent],
    queryFn: () => api.get('/groups/cities', {
      params: { q: q || undefined, continent: continent || undefined, limit: 80 },
    }).then(r => r.data),
    enabled: tab === 'cities',
  });

  const { data: countries } = useQuery({
    queryKey: ['browseCountries', continent],
    queryFn: () => api.get('/groups/countries', {
      params: { continent: continent || undefined },
    }).then(r => r.data),
    enabled: tab === 'countries',
  });

  const { data: trending } = useQuery({
    queryKey: ['trendingCities'],
    queryFn: () => api.get('/groups/trending').then(r => r.data),
    enabled: tab === 'trending',
  });

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-5 bg-gradient-to-br from-brand-500 to-grape-600 text-white">
        <h1 className="font-display text-2xl font-extrabold">Explore the world</h1>
        <p className="text-white/85 text-sm mt-1">
          Every city has its own room — like the old IRC days.
          Producers post offers, you rate them. <b>100% reach, no algorithm.</b>
        </p>
        {city && (
          <Link to={`/g/${encodeURIComponent(city.handle)}`}
            className="btn bg-white text-brand-700 btn-sm mt-3 font-extrabold">
            <MapPin size={13} /> My city: {city.handle}
          </Link>
        )}
      </div>

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100">
        {[['cities', '🏙️ Cities'], ['countries', '🌍 Countries'], ['trending', '🔥 Trending']]
          .map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition
                ${tab === k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>{l}</button>
          ))}
      </div>

      {tab !== 'trending' && (
        <div className="space-y-2 sticky top-14 z-20 py-2 -mx-3 px-3 bg-ink-50/95 backdrop-blur">
          {tab === 'cities' && (
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400" />
              <input className="input pl-10" placeholder="Search any city worldwide…"
                value={q} onChange={e => setQ(e.target.value)} />
            </div>
          )}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            <Chip active={!continent} onClick={() => setContinent('')} label="🌍 All" />
            {Object.values(CONTINENTS).map(c => (
              <Chip key={c.key} active={continent === c.key} onClick={() => setContinent(c.key)}
                label={`${c.emoji} ${c.label}`} />
            ))}
          </div>
        </div>
      )}

      {/* CITIES */}
      {tab === 'cities' && (
        isLoading ? <SkeletonList n={8} h="h-16" />
          : !cities?.length ? <Empty icon={Hash} title="No cities found" sub="Try a different search." />
          : <div className="grid sm:grid-cols-2 gap-2">
              {cities.map(g => <CityRow key={g.id} g={g} mine={city?.id === g.id} admin={isAdmin} />)}
            </div>
      )}

      {/* COUNTRIES */}
      {tab === 'countries' && (
        !countries ? <SkeletonList n={8} h="h-16" />
          : <div className="grid sm:grid-cols-2 gap-2">
              {countries.map(g => (
                <Link key={g.id} to={`/g/${encodeURIComponent(g.handle)}`}
                  className={`card-h p-3 flex items-center gap-3 ${country?.id === g.id ? 'ring-2 ring-brand-400' : ''}`}>
                  <span className="text-2xl shrink-0">{flagOf(g.country_code)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-extrabold text-sm truncate">{g.handle}</p>
                      {country?.id === g.id && <span className="chip-brand !text-[8px]">HOME</span>}
                    </div>
                    <p className="text-[11px] text-ink-400 font-semibold truncate">
                      {g.cities} cities
                      {g.population > 0 && ` · ${compact(g.population)} people`}
                      {admin(g) && ` · 👥 ${compact(g.members_count)}`}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
      )}

      {/* TRENDING */}
      {tab === 'trending' && (
        !trending ? <SkeletonList n={8} h="h-16" />
          : <div className="grid sm:grid-cols-2 gap-2">
              {trending.map((g, i) => (
                <Link key={g.id} to={`/g/${encodeURIComponent(g.handle)}`} className="card-h p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-red-500
                    grid place-items-center text-white font-black text-xs shrink-0">
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-sm truncate">{g.handle}</p>
                    <p className="text-[11px] text-ink-400 font-semibold truncate">
                      <TrendingUp size={9} className="inline" /> {g.recent_posts} posts this week
                      {g.population > 0 && ` · ${compact(g.population)} people`}
                    </p>
                  </div>
                  {g.shops > 0 && <span className="chip-mint shrink-0">🏪 {g.shops}</span>}
                </Link>
              ))}
            </div>
      )}
    </div>
  );

  function admin() { return isAdmin; }
}

function CityRow({ g, mine, admin }) {
  return (
    <Link to={`/g/${encodeURIComponent(g.handle)}`}
      className={`card-h p-3 flex items-center gap-3 ${mine ? 'ring-2 ring-brand-400' : ''}`}>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600
        grid place-items-center text-white shrink-0">
        {g.is_capital ? <span className="text-base">⭐</span> : <Hash size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-extrabold text-sm truncate">{g.handle}</p>
          {mine && <span className="chip-brand !text-[8px]">HOME</span>}
          <span className="text-xs">{flagOf(g.country_code)}</span>
        </div>
        <p className="text-[11px] text-ink-400 font-semibold truncate">
          {g.name}
          {g.population > 0 && ` · ${compact(g.population)} people`}
          {g.shops > 0 && ` · 🏪 ${g.shops}`}
          {admin && g.members_count > 0 && ` · 👥 ${compact(g.members_count)}`}
        </p>
      </div>
    </Link>
  );
}

function Chip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition
        ${active ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
      {label}
    </button>
  );
}